import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, ShieldCheck, Truck, Ruler } from "lucide-react";
import { toast } from "sonner";
import { inr, lineById } from "@/data/products";
import ZipCTA from "@/components/denim/ZipCTA";
import SizeGuide from "@/components/denim/SizeGuide";
import { cartAdd } from "@/components/denim/CartContext";
import { playZip } from "@/lib/zipSound";

const SIZES = ["28", "30", "32", "34", "36", "38"];

export default function QuickView({ p, onClose }) {
    const [size, setSize] = useState(null);
    const [ghost, setGhost] = useState(null);
    const [guide, setGuide] = useState(false);
    const line = lineById(p.line);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div data-testid="quickview-modal" className="fixed inset-0 z-[95] grid place-items-center p-4">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            />
            <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.98 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="relative z-10 grid max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-white/10 bg-coal md:grid-cols-2"
            >
                <button
                    data-testid="quickview-close"
                    onClick={onClose}
                    aria-label="Close quick view"
                    className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center border border-white/15 bg-black/60 text-ink backdrop-blur transition-colors hover:bg-gold hover:text-black"
                >
                    <X size={16} />
                </button>
                <div className="aspect-[3/4] max-h-[420px] w-full overflow-hidden bg-elev md:max-h-none">
                    <img src={p.img} alt={p.name} className="h-full w-full object-cover" />
                </div>
                <div className="flex flex-col p-7 md:p-9">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">
                        {line ? line.name : "URWAN"} · Limited Edition
                    </p>
                    <h3 className="mt-3 font-display text-2xl font-bold leading-tight text-ink md:text-3xl">
                        {p.name}
                    </h3>
                    <div className="mt-4 flex items-baseline gap-3">
                        <span className="font-display text-2xl font-semibold text-gold">{inr(p.now)}</span>
                        {p.now < p.was && (
                            <span className="text-sm text-mute line-through">{inr(p.was)}</span>
                        )}
                    </div>
                    <p className="mt-5 text-sm leading-relaxed text-mute">
                        {line ? line.sub : "Engineered to outlive trends"}. Patterned by data,
                        reinforced at the seams, and built to be lived in for years.
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-mute">
                            Select size
                        </p>
                        <button
                            data-testid="size-guide-open"
                            onClick={() => setGuide(true)}
                            className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-gold transition-colors hover:text-ink"
                        >
                            <Ruler size={12} /> Size Guide
                        </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {SIZES.map((s) => (
                            <button
                                key={s}
                                data-testid={`size-chip-${s}`}
                                onClick={() => setSize(s)}
                                className={`h-10 w-12 border font-mono text-sm transition-colors ${
                                    size === s
                                        ? "border-gold bg-gold text-black"
                                        : "border-white/15 text-ink hover:border-gold hover:text-gold"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <ZipCTA
                        type="button"
                        testid="quickview-add-button"
                        className="mt-7 w-full"
                        onClick={(e) => {
                            if (!size) {
                                toast.error("Please pick a size first");
                                return;
                            }
                            cartAdd({ productId: p.id, name: p.name, size, price: p.now, img: p.img });
                            playZip({ rate: 1.2, volume: 0.11 });
                            toast.success(`${p.name} (Size ${size}) dropped in your pocket`);
                            const fab = document.querySelector("[data-testid=pocket-cart-fab]");
                            const x0 = e.clientX || window.innerWidth / 2;
                            const y0 = e.clientY || window.innerHeight / 2;
                            if (fab) {
                                const r = fab.getBoundingClientRect();
                                setGhost({
                                    img: p.img,
                                    x: x0,
                                    y: y0,
                                    dx: r.left + r.width / 2 - x0,
                                    dy: r.top + r.height / 2 - y0,
                                });
                            }
                            setTimeout(onClose, 730);
                        }}
                    >
                        Add to Bag
                    </ZipCTA>
                    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-mono uppercase tracking-[0.15em] text-mute">
                        <span className="flex items-center gap-2">
                            <Truck size={14} className="text-gold" /> Free shipping
                        </span>
                        <span className="flex items-center gap-2">
                            <ShieldCheck size={14} className="text-gold" /> Lifetime stitch & tear warranty
                        </span>
                    </div>
                </div>
            </motion.div>
            {guide && <SizeGuide selected={size} onClose={() => setGuide(false)} />}
            {ghost && (
                <motion.img
                    src={ghost.img}
                    alt=""
                    initial={{ x: ghost.x - 40, y: ghost.y - 54, scale: 1, opacity: 1 }}
                    animate={{ x: ghost.x + ghost.dx - 8, y: ghost.y + ghost.dy - 8, scale: 0.18, opacity: 0.9 }}
                    transition={{ duration: 0.7, ease: [0.5, 0, 0.8, 0.4] }}
                    className="pointer-events-none fixed left-0 top-0 z-[130] h-24 w-20 border-2 border-gold object-cover"
                    onAnimationComplete={() => setGhost(null)}
                />
            )}
        </div>
    );
}
